// Механика экранов с задачами, общая для Projects, People и Launch: тост, карточка, метки присутствия,
// перетаскивание с режимом по дням, очередь клавиш, клонирование. Смысл (куда ляжет, что
// записать) остаётся в экране: targetFor / onDrop / plan.
import { escapeClosedTooltipOnly } from '@core/ui/Tooltip';
import { confirmDialog } from '@core/ui/confirm';
import { canWriteNow } from '@core/auth/access';
import { togglePin } from './pins';
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent, type RefObject } from 'react';
import { pushToast } from '../../../core/ui/notices';
import { data, useData, useTable } from '@core/store';
import { actorLabel } from '@core/people';
import { HOLIDAYS, daysBetween, today } from '@core/dates';
import { type Grid } from './geometry';
import { applyPlanned, type Planned } from './apply';
import { planBacklog, planDuplicate, planGroupMove } from './planner';
import { MoveQueue, arrowMove, type KeyMove } from './keyQueue';
import { assigneesNow, freshOf, rowKeyOf, rowTasksNow, rowTasksOf, taskNow, type Task } from './data';
import { draftFromTask, type CardTab, type Draft } from './cardEdits';
import type { DragKind } from './TaskBar';

export const PRESS_THRESHOLD = 4; // до этого — клик, не протяжка: двойной клик не путается с протяжкой

const friendly = (err: string | undefined) => {
  if (!err) return 'Failed';
  if (/conflict/i.test(err)) return 'Someone changed this just now — the screen is up to date, try again.';
  if (/fetch|network|Load failed/i.test(err)) return 'No connection — the change was not saved. Try again when back online.';
  return err;
};

/** Ошибка записи — красный тост; «X изменил задачу после тебя» — янтарный. Рисует NoticeStack в app.tsx. */
export function useToast(): { fail: (err?: string) => void } {
  const overridden = useData((s) => s.overridden);
  const profiles = useTable('profile');
  const people = useTable('person');
  // чужая правка по строке, которую я менял последние полминуты; показали — сняли, иначе всплывёт на каждом экране
  useEffect(() => {
    if (!overridden) return;
    if (overridden.table === 'task') {
      const t = taskNow(overridden.id);
      pushToast(`${actorLabel(overridden.actor, profiles, people).name} changed «${t?.title ?? 'a task'}» after you`, 'info');
    }
    data.store.setState({ overridden: null });
  }, [overridden]);
  const fail = useCallback((err?: string) => pushToast(friendly(err)), []);
  return { fail };
}

/** Левый край сегодняшнего дня в сетке или null, если он вне диапазона. */
export function useTodayX(grid: Grid): number | null {
  return useMemo(() => {
    const d = daysBetween(grid.start, today());
    return d < 0 || d >= grid.days ? null : d * grid.dayWidth;
  }, [grid]);
}

/** Нерабочие дни календаря (HOLIDAYS) как индексы дней от начала сетки — красятся как выходные. */
export function useHolidayDays(grid: Grid): number[] {
  return useMemo(() => HOLIDAYS.map((d) => daysBetween(grid.start, d)).filter((i) => i >= 0 && i < grid.days), [grid]);
}

/**
 * Escape на окне, пока active: снять выделение, подсветку. Не срабатывает в полях и там, где
 * Escape уже погасил Radix (меню, диалог): он вызывает preventDefault, но всплытие не останавливает.
 */
export function useEscape(active: boolean, onEscape: () => void) {
  const latest = useRef(onEscape);
  latest.current = onEscape;
  useEffect(() => {
    if (!active) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape' || (e.defaultPrevented && !escapeClosedTooltipOnly(e))) return; // Escape, закрывший меню, — не наш; закрывший подсказку — наш
      if (e.target instanceof HTMLElement && e.target.closest('input, textarea, [contenteditable]')) return;
      latest.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active]);
}

/** Свои записи, которые журнал ещё не подтвердил. */
export function usePendingIds(): Set<string> {
  const pending = useData((s) => s.pending);
  return useMemo(() => new Set(pending.map((p) => p.id)), [pending]);
}

/** Карточка задачи: открыть с исполнителями, закрыть; присутствие «редактирует». */
export function useTaskCard() {
  const assigneeRows = useTable('task_assignee');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [cardTab, setCardTab] = useState<CardTab>('task');
  const openTask = useCallback((t: Task, tab: CardTab = 'task') => {
    const a = [...assigneeRows.values()].filter((x) => x.task_id === t.id).sort((x, y) => x.position - y.position).map((x) => x.person_id);
    setCardTab(tab);
    setDraft(draftFromTask(t, a));
    data.updatePresence({ editing: t.id });
  }, [assigneeRows]);
  const closeModal = useCallback(() => { setDraft(null); data.updatePresence({ editing: null }); }, []);
  return { draft, setDraft, openTask, closeModal, cardTab };
}

/** Вопрос перед удалением: число — группа, строка — название одной. Один текст на все экраны. */
export const deleteQuestion = (what: number | string): string =>
  typeof what === 'number' ? `Delete ${what} tasks? They stay in the history and can be restored later.` : `Delete «${what}»? It stays in the history and can be restored later.`;

/** Вернуть фокус бару после перемонтирования, иначе следующая стрелка уйдёт в прокрутку. */
export const refocus = (selector: string) => requestAnimationFrame(() => {
  const el = document.querySelector<HTMLElement>(selector);
  if (el && document.activeElement !== el) el.focus({ preventScroll: true });
});

export function useDuplicate(fail: (err?: string) => void, after?: (newId: string) => void) {
  return useCallback(async (task: Task) => {
    const newId = crypto.randomUUID();
    const r = await applyPlanned(() => {
      const t = freshOf(task);
      return { changes: planDuplicate(t, assigneesNow(), rowTasksNow(rowKeyOf(t.project_id, t.subproject_id)), newId), op: { kind: 'duplicate_task', task_id: t.id, new_task_id: newId } };
    });
    if (!r.ok) fail(r.error);
    else after?.(newId);
  }, [fail, after]);
}

/** Действия над задачей из меню и хоткеев: закрепить, в бэклог и обратно, удалить. */
export function useTaskActions(fail: (err?: string) => void) {
  // пин — личный: дорожка в шапке видна только тому, кто закрепил
  const pin = useCallback(async (task: Task) => {
    try { await togglePin(task.id); } catch (e) { fail((e as Error).message); }
  }, [fail]);
  const backlog = useCallback(async (task: Task, inBacklog: boolean) => {
    const r = await applyPlanned(() => ({ changes: planBacklog(freshOf(task), inBacklog), op: { kind: inBacklog ? 'to_backlog' : 'from_backlog', task_id: task.id } }));
    if (!r.ok) fail(r.error);
  }, [fail]);
  const remove = useCallback(async (task: Task) => {
    if (!(await confirmDialog({ title: 'Delete task', text: deleteQuestion(task.title), yes: 'Delete', danger: true }))) return;
    const r = await data.applyChanges([{ table: 'task', id: task.id, delete: true }], { kind: 'delete_task', task_id: task.id });
    if (!r.ok) fail(r.error);
  }, [fail]);
  return { pin, backlog, remove };
}

// ---------------------------------------------------------------- автопрокрутка у края

const EDGE = 48;      // px от края тела, где начинается прокрутка
const EDGE_SPEED = 14; // px за кадр у самого края

/** Пока тянут у края тела сетки — прокручивать его; onTick — пересчитать место посадки. */
export function useEdgeScroll(gridRef: RefObject<HTMLDivElement | null>) {
  const raf = useRef<number | null>(null);
  const stop = useCallback(() => { if (raf.current !== null) cancelAnimationFrame(raf.current); raf.current = null; }, []);
  const track = useCallback((x: number, y: number, onTick: () => void) => {
    stop();
    const g = gridRef.current;
    if (!g) return;
    const r = g.getBoundingClientRect();
    const dy = y < r.top + EDGE ? -(r.top + EDGE - y) : y > r.bottom - EDGE ? y - (r.bottom - EDGE) : 0;
    const dx = x < r.left + EDGE ? -(r.left + EDGE - x) : x > r.right - EDGE ? x - (r.right - EDGE) : 0;
    if (!dy && !dx) return;
    const step = () => {
      g.scrollTop += Math.sign(dy) * Math.min(EDGE_SPEED, Math.abs(dy) / 3);
      g.scrollLeft += Math.sign(dx) * Math.min(EDGE_SPEED, Math.abs(dx) / 3);
      onTick();
      raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
  }, [gridRef, stop]);
  return useMemo(() => ({ track, stop }), [track, stop]);
}

// ---------------------------------------------------------------- перетаскивание

export type ResizePreview = { kind: 'resize'; start_date: string; end_date: string };

/** O — откуда тащим (Projects: из бэклога?; People: от кого), P — куда ляжет; T — строка задачи (роадмап или лонч). */
export type TaskDrag<O, P, T extends { id: string } = Task> = {
  kind: DragKind;
  task: T;
  group: string[];          // задача и всё выделение, если она в нём: сдвигаются вместе
  origin: O;
  startX: number;
  startY: number;
  dx: number;
  overRow: string | null;   // строка под указателем
  overBacklog: boolean;
  moved: boolean;
  day: boolean;             // Shift зажат: шаг по дням
  preview: P | null;
};

type DragOpts<O, P, T extends { id: string }> = {
  gridRef: RefObject<HTMLDivElement | null>;
  rowAttr: 'data-row' | 'data-person-row' | 'data-launch-row';
  table?: 'task' | 'launch_task';   // чья строка в руках: hold/release журнала по ней
  targetFor: (d: TaskDrag<O, P, T>, clientX: number, clientY: number, dx: number) => P | null;
  onDrop: (d: TaskDrag<O, P, T>, clientX: number, clientY: number, dx: number) => void | Promise<void>;
  focusSelector: (d: TaskDrag<O, P, T>) => string;
  /** Выделение экрана: Ctrl+клик переключает; нажатие на выделенный бар тащит всю группу, на невыделенный — выделяет только его. */
  selection?: { ids: ReadonlySet<string>; has: (id: string) => boolean; toggle: (id: string) => void; replace: (ids: Iterable<string>) => void };
};

export function useTaskDrag<O, P, T extends { id: string } = Task>({ gridRef, rowAttr, table = 'task', targetFor, onDrop, focusSelector, selection }: DragOpts<O, P, T>) {
  const [drag, setDrag] = useState<TaskDrag<O, P, T> | null>(null);
  const latest = useRef({ targetFor, onDrop, focusSelector, selection });
  latest.current = { targetFor, onDrop, focusSelector, selection };
  const edge = useEdgeScroll(gridRef);

  const onBarPointerDown = useCallback((task: T, origin: O, kind: DragKind, e: PointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    const sel = latest.current.selection;
    if (sel && kind === 'move' && (e.ctrlKey || e.metaKey)) { sel.toggle(task.id); return; }
    // клик по невыделенному бару — выделение из него одного: следующий Ctrl+клик добавит к нему, а не к пустоте
    if (sel && kind === 'move' && !sel.has(task.id)) sel.replace([task.id]);
    // наблюдатель выделяет и читает, но не двигает: выделение выше уже отработало
    if (!canWriteNow()) return;
    const group = sel && kind === 'move' && sel.has(task.id) && sel.ids.size > 1 ? [...sel.ids] : [task.id];
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag({ kind, task, group, origin, startX: e.clientX, startY: e.clientY, dx: 0, overRow: null, overBacklog: false, moved: false, day: e.shiftKey, preview: null });
    for (const id of group) data.hold(table, id);
    data.updatePresence({ dragging: group });
  }, [table]);

  useEffect(() => {
    if (!drag) return;
    let last = { x: drag.startX, y: drag.startY };
    const update = (x: number, y: number, day: boolean) => {
      const dx = x - drag.startX;
      const dy = y - drag.startY;
      const el = document.elementFromPoint(x, y);
      const overRow = el?.closest<HTMLElement>(`[${rowAttr}]`)?.getAttribute(rowAttr) ?? null;
      const overBacklog = !!el?.closest('[data-backlog]');
      const preview = overBacklog ? null : latest.current.targetFor({ ...drag, day }, x, y, dx);
      setDrag((d) => d && { ...d, dx, day, overRow, overBacklog, moved: d.moved || Math.abs(dx) + Math.abs(dy) > PRESS_THRESHOLD, preview });
    };
    let shift = false;
    const onMove = (e: globalThis.PointerEvent) => {
      last = { x: e.clientX, y: e.clientY }; shift = e.shiftKey;
      update(e.clientX, e.clientY, e.shiftKey);
      edge.track(e.clientX, e.clientY, () => update(last.x, last.y, shift));
    };
    // Shift нажали или отпустили, не двигая мышь — пересчитать место посадки
    const onShift = (e: globalThis.KeyboardEvent) => { if (e.key === 'Shift') { shift = e.type === 'keydown'; update(last.x, last.y, shift); } };
    const finish = () => {
      edge.stop();
      setDrag(null);
      for (const id of drag.group) data.release(table, id);
      data.updatePresence({ dragging: [] });
    };
    const onCancel = () => finish(); // тач или потеря захвата: задача на месте, без хвостов
    const onUp = (e: globalThis.PointerEvent) => {
      const d = { ...drag, day: e.shiftKey };
      finish();
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      // клик без движения — только выделить (фокус); карточка — двойной клик или Enter
      if (Math.abs(dx) + Math.abs(dy) <= PRESS_THRESHOLD) { if (d.kind === 'move') document.querySelector<HTMLElement>(latest.current.focusSelector(d))?.focus(); return; }
      void latest.current.onDrop(d, e.clientX, e.clientY, dx);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp, { once: true });
    window.addEventListener('pointercancel', onCancel, { once: true });
    window.addEventListener('keydown', onShift);
    window.addEventListener('keyup', onShift);
    return () => {
      edge.stop();
      window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown', onShift); window.removeEventListener('keyup', onShift);
    };
  }, [drag?.task.id]);

  return { dragPreview: drag && drag.moved ? drag : null, onBarPointerDown };
}

// ---------------------------------------------------------------- клавиатура

export type { KeyMove };

/** Группа роадмапа под стрелками: все на те же дни, треки строк пересчитываются. */
export const roadmapGroupPlan = (group: Task[], m: KeyMove): Planned => ({ changes: planGroupMove(group, m.days, rowTasksOf), op: { kind: 'move_tasks', task_ids: group.map((g) => g.id) } });

type KeysOpts<C, T extends { id: string }> = {
  openTask: (t: T) => void;
  duplicate: (t: T) => void | Promise<void>;
  pin?: (t: T) => void | Promise<void>;   // роадмап; в лонче закрепления нет
  remove: (t: T) => void | Promise<void>;
  /** План на подтверждённом состоянии; ctx — что экран хочет помнить между нажатиями (People: человек). */
  plan: (taskId: string, move: KeyMove, ctx: C) => { planned: Planned; ctx: C } | null;
  /** Выделение: задача в группе → стрелки двигают всю группу по времени (planGroup), вертикаль и растяжение не действуют. */
  groupOf?: (t: { id: string }) => T[] | null;
  planGroup?: (group: T[], move: KeyMove) => Planned | null;
  focusSelector: (taskId: string, ctx: C) => string;
  fail: (err?: string) => void;
};

/**
 * Клавиши на баре: Enter — карточка; Ctrl+D — копия; Ctrl+P — закрепить (где есть pin); Delete — удалить;
 * стрелки — ход через очередь (см. keyQueue): пока запись в полёте, нажатия копятся и уходят одной суммой.
 */
export function useTaskKeys<C, T extends { id: string } = Task>({ openTask, duplicate, pin, remove, plan, groupOf, planGroup, focusSelector, fail }: KeysOpts<C, T>) {
  const queue = useRef(new MoveQueue<C>());
  const latest = useRef({ plan, groupOf, planGroup, focusSelector, fail });
  latest.current = { plan, groupOf, planGroup, focusSelector, fail };

  const run = useCallback(async (taskId: string) => {
    const q = queue.current;
    if (q.isRunning(taskId)) return;
    q.setRunning(taskId, true);
    const focus = () => { const c = q.ctx(taskId); if (c !== undefined) refocus(latest.current.focusSelector(taskId, c)); };
    try {
      for (let move = q.take(taskId); move; move = q.take(taskId)) {
        const m = move;
        const r = await applyPlanned(() => {
          const group = latest.current.groupOf?.({ id: taskId });
          if (group) {
            const g = m.rows !== 0 || m.grow !== 0 ? null : latest.current.planGroup?.(group, m);
            return g ?? { changes: [], op: { kind: 'noop' } };
          }
          const p = latest.current.plan(taskId, m, q.ctx(taskId) as C);
          if (!p) return { changes: [], op: { kind: 'noop' } };
          q.setCtx(taskId, p.ctx);
          return p.planned;
        });
        focus();
        if (!r.ok) { latest.current.fail(r.error); break; }
        if (r.cancelled) { q.take(taskId); break; } // человек отказался в окне даты выхода — накопленные ходы тоже долой
      }
    } finally {
      q.setRunning(taskId, false);
      focus();
    }
  }, []);

  return useCallback((task: T, ctx: C, e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); openTask(task); return; }
    if (!canWriteNow()) return; // карточку наблюдатель открывает, остальные клавиши меняют план

    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') { e.preventDefault(); void duplicate(task); return; }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p' && pin) { e.preventDefault(); void pin(task); return; } // без pin (лонч) клавиша уходит браузеру
    if (e.key === 'Delete') { e.preventDefault(); void remove(task); return; }
    const move = arrowMove(e.key, { ctrl: e.ctrlKey || e.metaKey, shift: e.shiftKey });
    if (!move) return;
    e.preventDefault();
    queue.current.push(task.id, move, ctx);
    refocus(latest.current.focusSelector(task.id, queue.current.ctx(task.id) as C));
    void run(task.id);
  }, [openTask, duplicate, pin, remove, run]);
}

// ---------------------------------------------------------------- перестановка строк

export type RowDrag = { id: string; toIndex: number | null };

type ReorderOpts = {
  gridRef: RefObject<HTMLDivElement | null>;
  rowAttr: 'data-row' | 'data-person-row' | 'data-launch-row';
  /** Значение атрибута строки под курсором → id переставляемой сущности (или null: тут нельзя). */
  targetIdOf: (attr: string) => string | null;
  /** Селектор всех строк сущности: верх/низ считаются по блоку целиком, а не по строке под курсором. */
  blockSelector: (id: string) => string;
  /** Видимые переставляемые id в текущем порядке. */
  visibleIds: () => string[];
  /** Бросили: поставить fromId перед anchorId (null — в конец). */
  onDrop: (fromId: string, anchorId: string | null) => void;
};

/** Перетаскивание строки за ручку: индикатор места и вызов onDrop. Порядок общий — пишет экран. */
export function useRowReorder({ gridRef, rowAttr, targetIdOf, blockSelector, visibleIds, onDrop }: ReorderOpts) {
  const [rowDrag, setRowDrag] = useState<RowDrag | null>(null);
  const toIndex = useRef<number | null>(null); // последняя цель — над строкой-призраком она не меняется
  const latest = useRef({ targetIdOf, blockSelector, visibleIds, onDrop });
  latest.current = { targetIdOf, blockSelector, visibleIds, onDrop };
  const edge = useEdgeScroll(gridRef);

  const onHandlePointerDown = useCallback((id: string, e: PointerEvent<HTMLElement>) => {
    if (e.button !== 0 || !canWriteNow()) return;
    e.preventDefault();
    toIndex.current = null;
    setRowDrag({ id, toIndex: null });
  }, []);

  useEffect(() => {
    if (!rowDrag) return;
    const indexAt = (clientY: number): number | null => {
      const x = gridRef.current ? gridRef.current.getBoundingClientRect().left + 20 : 20;
      const hit = document.elementFromPoint(x, clientY);
      if (hit?.closest('[data-row-ghost]')) return toIndex.current;
      const rowEl = hit?.closest<HTMLElement>(`[${rowAttr}]`);
      const attr = rowEl?.getAttribute(rowAttr);
      const id = attr ? latest.current.targetIdOf(attr) : null;
      if (!id || !rowEl) return null;
      const list = latest.current.visibleIds();
      const i = list.indexOf(id);
      if (i < 0) return null;
      const rects = [...document.querySelectorAll(latest.current.blockSelector(id))].map((el) => el.getBoundingClientRect());
      const top = Math.min(...rects.map((r) => r.top)), bottom = Math.max(...rects.map((r) => r.bottom));
      return clientY > (top + bottom) / 2 ? i + 1 : i;
    };
    let lastY = 0;
    // новое состояние только при смене цели: автопрокрутка зовёт aim каждый кадр, а перерисовывать сетку 60 раз в секунду незачем
    const aim = (clientY: number) => { const next = indexAt(clientY); if (next === toIndex.current) return; toIndex.current = next; setRowDrag((d) => d && { ...d, toIndex: next }); };
    const onMove = (e: globalThis.PointerEvent) => {
      lastY = e.clientY;
      aim(e.clientY);
      edge.track(e.clientX, e.clientY, () => aim(lastY));
    };
    const onUp = (e: globalThis.PointerEvent) => {
      edge.stop();
      const d = rowDrag;
      setRowDrag(null);
      const to = indexAt(e.clientY);
      if (to === null) return;
      const visible = latest.current.visibleIds();
      const from = visible.indexOf(d.id);
      const adjusted = to > from ? to - 1 : to;
      if (adjusted === from) return; // бросили на своё место — записывать нечего
      // индекс среди видимых → место среди всех: ставим перед тем, кто сейчас на этом месте
      latest.current.onDrop(d.id, visible[adjusted] ?? null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp, { once: true });
    return () => { edge.stop(); window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
  }, [rowDrag?.id]);

  return { rowDrag, onHandlePointerDown };
}


// ---------------------------------------------------------------- нажатие-протяжка вне сетки

/**
 * Протяжка карточки или чипа (Release Plan) с теми же порогами, что у баров на сетке: до 4px это клик
 * (двойной клик не путается с протяжкой), без выделения текста, присутствие «в руках» на время.
 */
export function pressDrag(e: PointerEvent<HTMLElement>, taskId: string, h: { onMove: (dx: number, dy: number, ev: globalThis.PointerEvent) => void; onDrop: (ev: globalThis.PointerEvent) => void }) {
  if (e.button !== 0 || (e.target as HTMLElement).closest('button') || !canWriteNow()) return;
  e.preventDefault(); // иначе тянется выделение текста
  const x0 = e.clientX; const y0 = e.clientY;
  let moved = false;
  const onMove = (ev: globalThis.PointerEvent) => {
    if (!moved && Math.abs(ev.clientX - x0) + Math.abs(ev.clientY - y0) <= PRESS_THRESHOLD) return;
    if (!moved) { moved = true; data.updatePresence({ dragging: [taskId] }); document.body.classList.add('select-none'); }
    h.onMove(ev.clientX - x0, ev.clientY - y0, ev);
  };
  const finish = () => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp); window.removeEventListener('pointercancel', onCancel);
    if (!moved) return false;
    document.body.classList.remove('select-none');
    data.updatePresence({ dragging: [] });
    return true;
  };
  const onUp = (ev: globalThis.PointerEvent) => { if (finish()) h.onDrop(ev); };
  const onCancel = () => { finish(); }; // тач или потеря захвата: без броска, но и без хвостов
  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onCancel);
}
